import { useEffect, useRef } from 'react'
import type { MapRef } from 'react-map-gl/mapbox'
import { getGeoCoords } from 'cookies/geoCoordsCookie'
import getCurrentGeoLocation from 'lib/geo/navigatorUtils'
import { DEFAULT_VIEW_STATE } from './chatMapConstants'

interface UseMapGeolocationArgs {
  mapRef: React.RefObject<MapRef | null>
  isMapLoaded: boolean
  hasData: boolean
  shouldSkipAnimation: boolean
}

export function useMapGeolocation({
  mapRef,
  isMapLoaded,
  hasData,
  shouldSkipAnimation,
}: UseMapGeolocationArgs): void {
  const geoAttempted = useRef(false)
  const hasDataRef = useRef(hasData)
  const shouldSkipAnimationRef = useRef(shouldSkipAnimation)

  useEffect(() => {
    hasDataRef.current = hasData
  }, [hasData])

  useEffect(() => {
    shouldSkipAnimationRef.current = shouldSkipAnimation
  }, [shouldSkipAnimation])

  useEffect(() => {
    if (geoAttempted.current || hasData) return

    if (typeof document !== 'undefined') {
      const cached = getGeoCoords()
      if (cached) {
        // Don't set geoAttempted yet — this effect will re-run once isMapLoaded
        // becomes true, re-entering this branch to apply the cached position.
        if (!isMapLoaded) return
        geoAttempted.current = true
        const map = mapRef.current?.getMap()
        if (map) {
          map.jumpTo({
            center: [cached.longitude, cached.latitude],
            zoom: DEFAULT_VIEW_STATE.zoom,
            pitch: DEFAULT_VIEW_STATE.pitch,
            bearing: DEFAULT_VIEW_STATE.bearing,
          })
        }
        return
      }
    }

    if (!isMapLoaded) return
    geoAttempted.current = true

    getCurrentGeoLocation()
      .then(coords => {
        if (hasDataRef.current) return
        const map = mapRef.current?.getMap()
        if (!map) return
        if (shouldSkipAnimationRef.current) {
          map.jumpTo({
            center: [coords.longitude, coords.latitude],
            zoom: DEFAULT_VIEW_STATE.zoom,
            pitch: DEFAULT_VIEW_STATE.pitch,
            bearing: DEFAULT_VIEW_STATE.bearing,
          })
        } else {
          map.flyTo({
            center: [coords.longitude, coords.latitude],
            zoom: DEFAULT_VIEW_STATE.zoom,
            pitch: DEFAULT_VIEW_STATE.pitch,
            bearing: DEFAULT_VIEW_STATE.bearing,
            duration: 1500,
            essential: true,
          })
        }
      })
      .catch(() => {})
  }, [hasData, isMapLoaded, mapRef, shouldSkipAnimation])
}
